import { useEffect, useState } from "react";
import { CenterFlexRow, FlexRow } from "styles/layout";
import {
  checkTypeExhausted,
  comparePoints,
  compareTimes,
  matchingArrays,
  nonEmptyStrings,
  SortOrder,
} from "Utility";
import { getFrozenColumnOffset, StatTableColumn } from "./StatTableColumn";
import {
  SortArrowContainer,
  StatTableDataTr,
  StatTableHeaderTr,
  StatTableTable,
  StatTableTh,
  PaginationContainer,
  FilterInputContainer,
  LegendContainer,
  StatTableWrapper,
} from "./styles";
import { PaginationSwitcher } from "components/Pagination/PaginationSwitcher";
import { StatTableImageCell } from "./Columns/Image/ImageCell";
import { StatTableDateTimeCell } from "./Columns/DateTime/DateTimeCell";
import { StatTableLinkCell } from "./Columns/Link/LinkCell";
import { StatTableNumberCell } from "./Columns/Number/NumberCell";
import { StatTableOrgLogoNameCell } from "./Columns/OrgLogoName/OrgLogoNameCell";
import { StatTablePercentCell } from "./Columns/Percent/PercentCell";
import { StatTablePositionCell } from "./Columns/Position/PositionCell";
import { StatTableTextCell } from "./Columns/Text/TextCell";
import { StatTableTimeCell } from "./Columns/Time/TimeCell";
import { StatTablePointsCell } from "./Columns/Points/PointsCell";

const MIN_RECORDS_SEARCH_ENABLED = 12;

interface Props<T> {
  stats: T[];
  columns: StatTableColumn<T>[];
  keyGetter: (arg: T) => string | undefined;
  onRowClick?: (arg: T) => void;
  initialSortColumnIdx?: number;
  initialSortOrder?: SortOrder;
  thinner?: boolean;
  paginationAmount?: number;
  searchFunction?: (arg: T, searchText: string) => boolean;
  searchPlaceholder?: string;
  legend?: { color: string; text: string }[];
  primaryColor?: string;
  secondaryColor?: string;
  minRecordsSearchEnabled?: number;
}

function getSortColumn<T>(
  idx: number | undefined,
  columns: StatTableColumn<T>[]
) {
  if (idx === undefined) {
    return undefined;
  }

  return columns[idx];
}

function getSortedStats<T>(
  stats: T[],
  sortColumn: StatTableColumn<T> | undefined,
  sortOrder: SortOrder | undefined
) {
  return !!sortColumn
    ? sortStats(stats, sortColumn, sortOrder ?? "ASC")
    : stats;
}

export function StatTable<T>({
  stats,
  columns,
  keyGetter,
  onRowClick,
  initialSortColumnIdx,
  initialSortOrder,
  thinner,
  paginationAmount,
  searchFunction,
  searchPlaceholder,
  legend,
  primaryColor,
  secondaryColor,
  minRecordsSearchEnabled = MIN_RECORDS_SEARCH_ENABLED,
}: Props<T>) {
  // 1. Sort Stats
  var unmodifiedStatsCopy = stats.slice(0);
  var initialSortColumn = getSortColumn(initialSortColumnIdx, columns);
  var initialSortedStats = getSortedStats(
    unmodifiedStatsCopy,
    initialSortColumn,
    initialSortOrder
  );

  // Initialised as undefined because doesn't count initial sort
  const [currentSortColumn, setCurrentSortColumn] = useState<
    StatTableColumn<T> | undefined
  >();
  const [currentSortOrder, setCurrentSortOrder] = useState<SortOrder>();
  const [sortedStats, setSortedStats] = useState<T[]>(initialSortedStats);

  // todo - clean this up, required for track stats
  useEffect(() => {
    unmodifiedStatsCopy = stats.slice(0);
    initialSortColumn =
      initialSortColumnIdx !== undefined
        ? columns[initialSortColumnIdx]
        : undefined;

    initialSortedStats = !!initialSortColumn
      ? sortStats(
          unmodifiedStatsCopy,
          initialSortColumn,
          initialSortOrder ?? "ASC"
        )
      : unmodifiedStatsCopy;

    const resetColumnSort =
      !currentSortColumn || !columns.includes(currentSortColumn);
    const sortColumn = !resetColumnSort
      ? currentSortColumn
      : initialSortColumnIdx !== undefined
      ? columns[initialSortColumnIdx]
      : undefined;
    const sortOrder = !resetColumnSort ? currentSortOrder : initialSortOrder;
    setSortedStats(
      !!sortColumn
        ? sortStats(unmodifiedStatsCopy, sortColumn, sortOrder ?? "ASC")
        : unmodifiedStatsCopy
    );
  }, [stats]);

  // If Columns or InitialSortColumnIdx change check if we need to resort the stats
  useEffect(() => {
    initialSortColumn = getSortColumn(initialSortColumnIdx, columns);
    initialSortedStats = sortStats(
      unmodifiedStatsCopy,
      initialSortColumn,
      initialSortOrder ?? "ASC"
    );

    // If the sorted stats have changed, update state
    if (!matchingArrays(initialSortedStats, sortedStats)) {
      setSortedStats(initialSortedStats);
      setCurrentSortColumn(undefined);
      setCurrentSortOrder(undefined);
    }
  }, [initialSortColumnIdx, columns]);

  // 2. Filter Stats if records are above minRecordsSearchEnabled
  const [searchText, setSearchText] = useState("");
  const [filteredStats, setFilteredStats] = useState<T[]>(
    filterStats(
      initialSortedStats,
      searchFunction,
      searchText,
      minRecordsSearchEnabled
    )
  );

  // If SearchText or SortedStats state are updated, refilter the stats
  useEffect(() => {
    const newFilteredStats = filterStats(
      sortedStats,
      searchFunction,
      searchText,
      minRecordsSearchEnabled
    );

    // If the filtered stats have changed, update state
    if (!matchingArrays(newFilteredStats, filteredStats)) {
      setFilteredStats(newFilteredStats);
    }
  }, [searchText, sortedStats]);

  // 3. Paginate Stats
  const [selectedPage, setSelectedPage] = useState(1);
  var maxPage = !!paginationAmount
    ? Math.ceil(filteredStats.length / paginationAmount)
    : undefined;

  useEffect(() => {
    if (!!maxPage && selectedPage > maxPage) {
      setSelectedPage(maxPage);
    }
  }, [maxPage]);

  const [paginatedStats, setPaginatedStats] = useState<T[]>(
    paginateStats(filteredStats, paginationAmount, selectedPage)
  );

  // Scroll to top of page on pagination switch
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedPage]);

  // If SelectedPage or FilteredStats state are updated,
  useEffect(() => {
    const newPaginatedStats = paginateStats(
      filteredStats,
      paginationAmount,
      selectedPage
    );
    // If the paginated stats have changed, update state
    if (!matchingArrays(newPaginatedStats, paginatedStats)) {
      setPaginatedStats(newPaginatedStats);
    }
  }, [selectedPage, filteredStats]);

  const onRowClickInternal = (e: any, arg: T) => {
    if (e.target.id !== "link_button") {
      if (!!onRowClick) {
        onRowClick(arg);
      }
    }
  };

  return (
    <>
      {!!searchFunction && sortedStats.length > minRecordsSearchEnabled && (
        <FilterInputContainer>
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value ?? "")}
            placeholder={searchPlaceholder}
            spellCheck={false}
          />
        </FilterInputContainer>
      )}
      <StatTableWrapper>
        <StatTableTable>
          <thead>
            <StatTableHeaderTr thinner={thinner === true}>
              {columns.map((col, i) => (
                <StatTableTh
                  key={`${i} ${col.title}`}
                  highlight={col.highlight}
                  highlightSort={col === currentSortColumn}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  title={col.title}
                  clickable={col.sortable}
                  frozenWidth={col.frozenWidth}
                  frozenOffset={getFrozenColumnOffset(columns, col, i)}
                  onClick={
                    !!col.sortable
                      ? () =>
                          setSortedStats(
                            sortByColumn(
                              unmodifiedStatsCopy,
                              initialSortedStats,
                              col,
                              col === currentSortColumn,
                              setCurrentSortColumn,
                              currentSortOrder,
                              setCurrentSortOrder
                            )
                          )
                      : undefined
                  }
                >
                  <CenterFlexRow>
                    <SortArrowContainer />
                    {col.header}
                    <SortArrowContainer
                      style={!col.header ? { marginLeft: "auto" } : {}}
                    >
                      {currentSortColumn === col
                        ? getSortArrow(currentSortOrder)
                        : ""}
                    </SortArrowContainer>
                  </CenterFlexRow>
                </StatTableTh>
              ))}
            </StatTableHeaderTr>
          </thead>
          <tbody>
            {paginatedStats.map((stat, i) => (
              <StatTableDataTr
                key={`${keyGetter(stat)}-${i}`}
                thinner={thinner === true}
                clickable={!!onRowClick}
                onClick={
                  !!onRowClick ? (e) => onRowClickInternal(e, stat) : undefined
                }
              >
                {columns.map((col, i) =>
                  mapStatToColumn(
                    stat,
                    col,
                    i,
                    getFrozenColumnOffset(columns, col, i)
                  )
                )}
              </StatTableDataTr>
            ))}
          </tbody>
        </StatTableTable>
      </StatTableWrapper>
      {!!maxPage && maxPage > 1 && (
        <PaginationContainer>
          <PaginationSwitcher
            selectedPage={selectedPage}
            setSelectedPage={setSelectedPage}
            maxPage={maxPage}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
        </PaginationContainer>
      )}
      {!!legend && (
        <LegendContainer>
          <FlexRow style={{ padding: "0 20px", columnGap: "10px" }}>
            {legend.map((l) => (
              <FlexRow style={{ columnGap: "5px" }}>
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    border: "0.5px solid darkgray",
                    background: l.color,
                  }}
                />
                <span style={{ fontSize: "10pt" }}>{l.text}</span>
              </FlexRow>
            ))}
          </FlexRow>
        </LegendContainer>
      )}
    </>
  );
}

function mapStatToColumn<T>(
  stats: T,
  column: StatTableColumn<T>,
  columnIdx: number,
  frozenOffset: number | undefined
) {
  const props = {
    stats,
    columnIdx,
    frozenOffset,
  };

  switch (column.type) {
    case "DateTime":
      return <StatTableDateTimeCell {...props} column={column} />;
    case "Image":
      return <StatTableImageCell {...props} column={column} />;
    case "Link":
      return <StatTableLinkCell {...props} column={column} />;
    case "Number":
      return <StatTableNumberCell {...props} column={column} />;
    case "OrgLogoName":
      return <StatTableOrgLogoNameCell {...props} column={column} />;
    case "Percent":
      return <StatTablePercentCell {...props} column={column} />;
    case "Points":
      return <StatTablePointsCell {...props} column={column} />;
    case "Position":
      return <StatTablePositionCell {...props} column={column} />;
    case "Text":
      return <StatTableTextCell {...props} column={column} />;
    case "Time":
      return <StatTableTimeCell {...props} column={column} />;
    default:
      checkTypeExhausted(column);
      return <></>;
  }
}

const getSortArrow = (sortOrder: SortOrder | undefined) => {
  if (sortOrder === "ASC") {
    return "▲";
  }

  if (sortOrder === "DESC") {
    return "▼";
  }
};

function sortStats<T>(
  stats: T[],
  column: StatTableColumn<T> | undefined,
  order: SortOrder
) {
  if (order === undefined || column === undefined) {
    return stats;
  }

  const sortAscending = order === "ASC";

  switch (column.type) {
    case "DateTime":
      sortByNumberGetter(stats, column.dateTimeGetter, sortAscending);
      break;
    case "Image":
      if (!!column.imageTextGetter) {
        sortByTextGetter(stats, column.imageTextGetter, sortAscending);
      }
      break;
    case "Link":
      sortByTextGetter(stats, column.linkGetter, sortAscending);
      break;
    case "Number":
      sortByNumberGetter(stats, column.valueGetter, sortAscending);
      break;
    case "OrgLogoName":
      sortByTextGetter(stats, column.nameGetter, sortAscending);
      break;
    case "Percent":
      sortByNumberGetter(stats, column.rateGetter, sortAscending);
      break;
    case "Points":
      sortByPointsGetter(stats, column.pointsGetter, sortAscending);
      break;
    case "Position":
      sortByNumberGetter(stats, column.positionGetter, sortAscending);
      break;
    case "Text":
      sortByTextGetter(stats, column.textGetter, sortAscending);
      break;
    case "Time":
      sortByTimeGetter(stats, column.timeGetter, sortAscending);
      break;
    default:
      checkTypeExhausted(column);
  }

  return stats;
}

function sortByColumn<T>(
  stats: T[],
  initialSortedStats: T[],
  column: StatTableColumn<T>,
  isCurrentSortColumn: boolean,
  setCurrentSortColumn: React.Dispatch<
    React.SetStateAction<StatTableColumn<T> | undefined>
  >,
  currentSortOrder: SortOrder | undefined,
  setCurrentSortOrder: React.Dispatch<
    React.SetStateAction<SortOrder | undefined>
  >
) {
  let newSortOrder: SortOrder | undefined;
  const initialSort = column.sortDescFirst ? "DESC" : "ASC";
  // If unsorted sort by the initial sort
  if (!isCurrentSortColumn || currentSortOrder === undefined) {
    newSortOrder = initialSort;
    // If sorted by the initial sort, sort by the other way
    // else revert to default sort of the table (undefined)
  } else if (currentSortOrder === initialSort) {
    newSortOrder = initialSort === "ASC" ? "DESC" : "ASC";
  }

  const sortedStats = !!newSortOrder
    ? sortStats(stats, column, newSortOrder)
    : initialSortedStats;

  const newSortColumn = !!newSortOrder ? column : undefined;
  setCurrentSortOrder(newSortOrder);
  setCurrentSortColumn(newSortColumn);
  return sortedStats;
}

function sortByNumberGetter<T>(
  stats: T[],
  getter: (arg: T) => number | undefined,
  ascending: boolean
) {
  return stats.sort((a, b) => {
    const valA = getter(a);
    const valB = getter(b);
    if (valA === valB) {
      return 0;
    }

    // Sort undefined values to the bottom always
    if (valA === undefined) {
      return 1;
    }

    // Sort undefined values to the bottom always
    if (valB === undefined) {
      return -1;
    }

    return ascending ? valA - valB : valB - valA;
  });
}

function sortByPointsGetter<T>(
  stats: T[],
  getter: (arg: T) => number | "Qual" | undefined,
  ascending: boolean
) {
  return stats.sort((a, b) =>
    comparePoints(getter(a), getter(b), ascending ? "ASC" : "DESC")
  );
}

function sortByTimeGetter<T>(
  stats: T[],
  getter: (arg: T) => number | undefined,
  ascending: boolean
) {
  return stats.sort((a, b) =>
    compareTimes(getter(a), getter(b), ascending ? "ASC" : "DESC")
  );
}

function sortByTextGetter<T>(
  stats: T[],
  getter: (arg: T) => string | undefined,
  ascending: boolean
) {
  if (ascending) {
    return stats.sort((a, b) =>
      (getter(a) ?? "").localeCompare(getter(b) ?? "")
    );
  }

  return stats.sort((a, b) => (getter(b) ?? "").localeCompare(getter(a) ?? ""));
}

function filterStats<T>(
  sortedStats: T[],
  searchFunction: ((arg: T, searchText: string) => boolean) | undefined,
  searchText: string,
  minRecordsSearchEnabled: number
) {
  if (
    !searchFunction ||
    !searchText ||
    sortedStats.length < minRecordsSearchEnabled
  ) {
    return sortedStats;
  }

  var filteredStats = [...sortedStats];
  const splitSearchText = nonEmptyStrings(searchText.split(","));
  filteredStats = filteredStats.filter((stat) =>
    splitSearchText.some((text) => searchFunction(stat, text.trim()))
  );

  return filteredStats;
}

function paginateStats<T>(
  filteredStats: T[],
  amountPerPage: number | undefined,
  selectedPage: number
) {
  if (!amountPerPage) {
    return filteredStats;
  }

  const firstIdx = (selectedPage - 1) * amountPerPage;
  const lastIdx = firstIdx + amountPerPage;
  return filteredStats.slice(firstIdx, lastIdx);
}
